import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, IConfirmDialog } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ISelectItems } from 'app/models/shared/shared';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class SharedService {
  public mobile: boolean = false;
  public tablet: boolean = false;
  public development: boolean = environment.build === 'Development';
  private api: string = environment.privateApi;
  private changeTheme: Subject<string> = new Subject<string>();
  public darkMode: boolean = false;
  public theme: string = 'light';
  public countyAccess: string[] = []

  /**
   * Color Codes
   */
  public purple: string = '#9355dc';
  public blue: string = '#628ef4';
  public gold: string = '#ffbe2e';
  public green: string = '#70e17b';
  public pink: string = '#e9695f';
  public orange: string = '#EC891D';
  public red: string = '#fc4132';
  public loading: boolean = false;
  public today: Date = new Date(Date.now());

  /**
   * License Types
   */
  public individualLicense: string = 'IndividualLicense';
  public individualApplicant: string = 'IndividualApplicant';
  public entityLicense: string = 'EntityLicense';
  public entityApplicant: string = 'EntityApplicant';

  public userCountyList: ISelectItems[] = []
  /*
   * Statuses
   */
  public started: string = "Started";
  public submitted: string = "Submitted";
  public submittedForApproval: string = 'Submitted for Approval';
  public resubmitted: string = "Resubmitted";
  public awaitingPayment: string = "Approved, Pending Payment and Clearance";
  public approved: string = "Approved";
  public incomplete: string = "Incomplete";
  public insufficient: string = "Insufficient";
  public underReview: string = "Under Review";
  public denied: string = "Denied";
  public revoked: string = "Revoked";
  public expired: string = "Expired";
  public withdrawn: string = "Withdrawn";
  public surrendered: string = "Surrendered";
  public closed: string = 'Closed';

  stateList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
    "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
    "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "American Samoa",
    "Commonwealth of the Northern Mariana Islands", "Guam", "Puerto Rico", "US Virgin Islands",
    "Federated States of Micronesia", "Republic of the Marshall Islands", "Republic of Palau"
  ];

  countyDropDownList: ISelectItems[] = [
    { "value": "001", "text": "Baker" },
    { "value": "003", "text": "Benton" },
    { "value": "005", "text": "Clackamas" },
    { "value": "007", "text": "Clatsop" },
    { "value": "009", "text": "Columbia" },
    { "value": "011", "text": "Coos" },
    { "value": "013", "text": "Crook" },
    { "value": "015", "text": "Curry" },
    { "value": "017", "text": "Deschutes" },
    { "value": "019", "text": "Douglas" },
    { "value": "021", "text": "Gilliam" },
    { "value": "023", "text": "Grant" },
    { "value": "025", "text": "Harney" },
    { "value": "027", "text": "Hood River" },
    { "value": "029", "text": "Jackson" },
    { "value": "031", "text": "Jefferson" },
    { "value": "033", "text": "Josephine" },
    { "value": "035", "text": "Klamath" },
    { "value": "037", "text": "Lake" },
    { "value": "039", "text": "Lane" },
    { "value": "041", "text": "Lincoln" },
    { "value": "043", "text": "Linn" },
    { "value": "045", "text": "Malheur" },
    { "value": "047", "text": "Marion" },
    { "value": "049", "text": "Morrow" },
    { "value": "051", "text": "Multnomah" },
    { "value": "053", "text": "Polk" },
    { "value": "055", "text": "Sherman" },
    { "value": "057", "text": "Tillamook" },
    { "value": "059", "text": "Umatilla" },
    { "value": "061", "text": "Union" },
    { "value": "063", "text": "Wallowa" },
    { "value": "065", "text": "Wasco" },
    { "value": "067", "text": "Washington" },
    { "value": "069", "text": "Wheeler" },
    { "value": "071", "text": "Yamhill" }
  ];

  businessTypeDropDownList: ISelectItems[] = [
    { "value": "0", "text": "" },
    { "value": "1", "text": "Assisted living facility" },
    { "value": "2", "text": "Automotive shop" },
    { "value": "3", "text": "Bar/Tavern" },
    { "value": "4", "text": "Bar/lounge area of restaurant" },
    { "value": "5", "text": "Bingo hall" },
    { "value": "6", "text": "Bowling center" },
    { "value": "7", "text": "Cigar bar" },
    { "value": "8", "text": "Educational facility", },
    { "value": "9", "text": "Gas station" },
    { "value": "10", "text": "Hospital" },
    { "value": "11", "text": "Hotel/motel" },
    { "value": "12", "text": "Office building" },
    { "value": "13", "text": "Pool hall" },
    { "value": "14", "text": "Private/fraternal organization" },
    { "value": "15", "text": "Restaurant" },
    { "value": "16", "text": "Retail store" },
    { "value": "17", "text": "Smoke shop" },
    { "value": "18", "text": "Warehouse" },
    { "value": "19", "text": "Other" }
  ];

  nonActionableList: ISelectItems[] = [
    { "value": "14", "text": "Exempt (business is a certified cigar bar/smoke shop)" },
    { "value": "13", "text": "Not valid (not unlawful as described by complainant)" },
    { "value": "3", "text": "Grace Period" },
    { "value": "13", "text": "Business Inactive" }
  ];

  countyList: string[] = [
    "Baker", "Benton", "Clackamas", "Clatsop", "Columbia", "Coos", "Crook",
    "Curry", "Deschutes", "Douglas", "Gilliam", "Grant", "Harney", "Hood River",
    "Jackson", "Jefferson", "Josephine", "Klamath", "Lake", "Lane", "Lincoln",
    "Linn", "Malheur", "Marion", "Morrow", "Multnomah", "Polk", "Sherman",
    "Tillamook", "Umatilla", "Union", "Wallowa", "Wasco", "Washington", "Wheeler",
    "Yamhill"
  ];

  stateDropdownList: ISelectItems[] = [
    { "value": "01", "text": "AL" },
    { "value": "02", "text": "AK" },
    { "value": "04", "text": "AZ" },
    { "value": "05", "text": "AR" },
    { "value": "06", "text": "CA" },
    { "value": "08", "text": "CO" },
    { "value": "09", "text": "CT" },
    { "value": "11", "text": "DE" },
    { "value": "12", "text": "FL" },
    { "value": "13", "text": "GA" },
    { "value": "15", "text": "HI" },
    { "value": "16", "text": "ID" },
    { "value": "17", "text": "IL" },
    { "value": "18", "text": "IN" },
    { "value": "19", "text": "IA" },
    { "value": "20", "text": "KS" },
    { "value": "21", "text": "KY" },
    { "value": "22", "text": "LA" },
    { "value": "23", "text": "ME" },
    { "value": "24", "text": "MD" },
    { "value": "25", "text": "MA" },
    { "value": "26", "text": "MI" },
    { "value": "27", "text": "MN" },
    { "value": "28", "text": "MS" },
    { "value": "29", "text": "MO" },
    { "value": "30", "text": "MT" },
    { "value": "31", "text": "NE" },
    { "value": "32", "text": "NV" },
    { "value": "33", "text": "NH" },
    { "value": "34", "text": "NJ" },
    { "value": "35", "text": "NM" },
    { "value": "36", "text": "NY" },
    { "value": "37", "text": "NC" },
    { "value": "38", "text": "ND" },
    { "value": "39", "text": "OH" },
    { "value": "40", "text": "OK" },
    { "value": "41", "text": "OR" },
    { "value": "42", "text": "PA" },
    { "value": "44", "text": "RI" },
    { "value": "45", "text": "SC" },
    { "value": "46", "text": "SD" },
    { "value": "47", "text": "TN" },
    { "value": "48", "text": "TX" },
    { "value": "49", "text": "UT" },
    { "value": "50", "text": "VT" },
    { "value": "51", "text": "VA" },
    { "value": "53", "text": "WA" },
    { "value": "54", "text": "WV" },
    { "value": "55", "text": "WI" },
    { "value": "56", "text": "WY" }
  ];

  statesList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
    "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
    "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
    "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI",
    "WY", "AS", "GU", "MP", "PR", "UM", "VI"
  ];

  correspondenceType: ISelectItems[] = [
    { "value": "1", "text": "Complainant Letter" },
    { "value": "2", "text": "Business IRL" },
    { "value": "3", "text": "Custom Business Letter" },
    { "value": "4", "text": "Misc." },
    { "value": "20", "text": "Misc." }
    
  ];

  loremIpsum: string = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
  incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
  laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit
  esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt
  in culpa qui officia deserunt mollit anim id est laborum.`

  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  public confirmDialogRef: MatDialogRef<ConfirmDialogComponent>;

  private httpClient = inject(HttpClient);
  public dialog = inject(MatDialog);
  public toastr = inject(ToastrService);

  public openNotice(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: false
    }
    this.openDialog(data);
  }

  public openConfirm(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: true
    }
    this.openDialog(data);
  }

  openDialog(data: IConfirmDialog): void {
    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: data,
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.mobile ? '700px' : '300px',
      maxHeight: this.mobile ? '600px' : '700px',
      panelClass: this.theme === "dark" ? "theme-dark" : "",
    });
  }

  public confirmed(): Observable<any> {
    return this.confirmDialogRef.afterClosed().pipe(take(1), map(response => {
      return response;
    }));
  }

  public setUserCountyList(counties: string[]): void {
    this.userCountyList = [];
    counties.forEach(county => {
      this.userCountyList.push(this.countyDropDownList.find(x => x.value == county));
    });
  }

  public convertCounty(type: string): string {
    return (type == null || type.length == 0) ? '' : this.countyDropDownList.find(x => x.value == type)?.text;
  }

  public getAttachment(id: number): Observable<Blob> {
    return this.http.get<Blob>(this.api + 'ComplaintStaff/getAttachment/' + id, { responseType: 'blob' as 'json' });
  }

  public downloadFile(fileName: string, blob: Blob) {
    let file = URL.createObjectURL(blob);
    var fileDownload = document.createElement('a');
    fileDownload.href = file;
    fileDownload.download = fileName;
    fileDownload.click();
  }

  public downloadFile2(blob: Blob) {
    let file = URL.createObjectURL(blob);
    var fileDownload = document.createElement('a');
    fileDownload.href = file;
    fileDownload.click();
  }

  public downloadCSV(csvContent: string, fileName: string) {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', fileName);
    a.style.display = 'none';
    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  get isMobile(): boolean {
    return this.mobile;
  }

  get counties(): string[] {
    return this.countyList;
  }

  get states(): string[] {
    return this.stateList;
  }

  themeChanged$ = this.changeTheme.asObservable();

  getUserTheme(): void {
    this.httpClient.get<string>(this.api + "UserProfile/GetUserTheme", { responseType: 'text' as 'json' }).subscribe({
      next: response => {
        this.theme = response;
        this.toggleTheme();
      },
      error: e => console.log('error getting user theme', e)
    });
  }

  updateUserTheme(): void {
    this.httpClient.post<any>(this.api + "UserProfile/UpdateUserTheme/" + this.theme, null).subscribe(
      () => { },
      error => console.log('error', error)
    );
  }

  copyToClipboard(text: string): void {
    // navigator.clipboard.writeText(text);
    let item = new ClipboardItem({ "text/html": new Blob([text], { type: "text/html" }) });
    navigator.clipboard.write([item]);
    this.toastr.success("Copied!");
  }

  toggleTheme() {
    this.darkMode = this.theme === 'dark';
    this.changeTheme.next(this.theme);
  }

  consoleLog(error: any): void {
    if (this.development) {
      console.log('error', error);
    }
  }

  isCancelled(response: any): boolean {
    return response === 'cancel' || response === null || response === '' || response === undefined;
  }

  getDateFromString(date: string): Date {
    const dateComponents = date.split('/');
    const month = parseInt(dateComponents[0], 10);
    const day = parseInt(dateComponents[1], 10);
    const year = parseInt(dateComponents[2], 10);
    return new Date(year, month - 1, day);
  }

  doRedirect(url: string) {
    this.router.navigateByUrl(url);
  }

  downloadHelpFile(type: string): Observable<Blob> {
    return this.httpClient.get<Blob>(this.api + 'downloadHelpFile/' + type + '/PDF', { responseType: 'blob' as 'json' });
  }

  getBusinessType(type: string): string {
    return (type == null || type.length == 0) ? '' : this.businessTypeDropDownList.find(x => x.value == type).text;
  }

  getCorrespondenceType(type: string): string {
    return (type == null || type.length == 0) ? '' : this.correspondenceType.find(x => x.value == type).text;
  }

  getCountyList(): Observable<string[]>{
    return this.http.get<string[]>(this.api + 'MainMenu/getCountyList');
  }

}
